import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
  type SelectProps,
  type TableProps,
} from '@cloudscape-design/components';
import {
  fetchProducts,
  fetchVersions,
  createVersion,
  updateVersion,
  type ProductRegistryEntry,
  type StackVersion,
  type VersionStatus,
} from '../api/stack-versions';

/**
 * Version lifecycle:
 *   draft   → "Latest"    — just registered, not yet promoted for production use
 *   current → "Stable"    — production-ready, new deploys use this by default
 *   previous → "Previous" — was stable, superseded by a newer stable
 *   deprecated → hidden   — end-of-life, filtered from all views
 */
const STATUS_DISPLAY: Record<VersionStatus, { label: string; badge: 'green' | 'blue' | 'grey' | 'red'; description: string }> = {
  current:    { label: 'Stable',     badge: 'green', description: 'Production-ready. Default for new deploys.' },
  draft:      { label: 'Latest',     badge: 'blue',  description: 'Registered but not yet promoted to stable.' },
  previous:   { label: 'Previous',   badge: 'grey',  description: 'Was stable, superseded by a newer version.' },
  deprecated: { label: 'Deprecated', badge: 'red',   description: 'End-of-life. Hidden from deploy screens.' },
};

export function StackVersions() {
  const [products, setProducts] = useState<Record<string, ProductRegistryEntry>>({});
  const [selectedProduct, setSelectedProduct] = useState<SelectProps.Option | null>(null);
  const [versions, setVersions] = useState<StackVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newChangelog, setNewChangelog] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProducts().then((res) => {
      setProducts(res.products);
      const first = Object.values(res.products)[0];
      if (first) {
        setSelectedProduct({ label: first.productName, value: first.productId, description: first.description });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedProduct?.value) return;
    loadVersions(selectedProduct.value);
  }, [selectedProduct]);


  function loadVersions(productId: string): void {
    setLoading(true);
    setError(null);
    fetchVersions(productId)
      .then((res) => setVersions(res.versions))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  async function handleCreate(): Promise<void> {
    if (!selectedProduct?.value || !newVersion) return;
    setSubmitting(true);
    try {
      const v = await createVersion(selectedProduct.value, {
        version: newVersion, description: newDesc, changelog: newChangelog,
      });
      setVersions((prev) => [v, ...prev]);
      setShowCreate(false);
      setNewVersion(''); setNewDesc(''); setNewChangelog('');
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed to create version'); }
    finally { setSubmitting(false); }
  }

  async function handlePromote(v: StackVersion): Promise<void> {
    try {
      await updateVersion(v.productId, v.version, { status: 'current' });
      loadVersions(v.productId);
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed to promote'); }
  }

  async function handleDeprecate(v: StackVersion): Promise<void> {
    try {
      await updateVersion(v.productId, v.version, { status: 'deprecated' });
      loadVersions(v.productId);
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed to deprecate'); }
  }

  const productOptions: SelectProps.Options = Object.values(products).map((p) => ({
    label: p.productName, value: p.productId, description: p.description,
  }));

  const visibleVersions = versions.filter((v) => v.status !== 'deprecated');
  const stableVersion = versions.find((v) => v.status === 'current');
  const latestVersion = versions.find((v) => v.status === 'draft');
  const previousVersions = versions.filter((v) => v.status === 'previous');
  const totalDeployed = versions.reduce((sum, v) => sum + v.tenantsDeployed, 0);

  const columns: TableProps.ColumnDefinition<StackVersion>[] = [
    {
      id: 'version', header: 'Version', width: 110,
      cell: (item) => <code style={{ fontWeight: 700, fontSize: 13 }}>{item.version}</code>,
    },
    {
      id: 'status', header: 'Status', width: 120,
      cell: (item) => {
        const cfg = STATUS_DISPLAY[item.status];
        return <Badge color={cfg.badge}>{cfg.label}</Badge>;
      },
    },
    { id: 'description', header: 'Description', cell: (item) => item.description || '—' },
    { id: 'changelog', header: 'Changelog', cell: (item) => item.changelog || '—' },
    {
      id: 'tenants', header: 'Deployed', width: 90,
      cell: (item) => (
        <StatusIndicator type={item.tenantsDeployed > 0 ? 'info' : 'stopped'}>
          {item.tenantsDeployed}
        </StatusIndicator>
      ),
    },
    {
      id: 'created', header: 'Registered', width: 110,
      cell: (item) => new Date(item.createdAt).toLocaleDateString(),
    },
    {
      id: 'actions', header: '', width: 140,
      cell: (item) => {
        const canPromote = item.status === 'draft' || item.status === 'previous';
        const canDeprecate = item.status !== 'deprecated';
        return (
          <SpaceBetween direction="horizontal" size="xs">
            {canPromote && (
              <Button variant="inline-link" onClick={() => handlePromote(item)}>
                Promote to Stable
              </Button>
            )}
            {item.status === 'current' && (
              <Box fontSize="body-s" color="text-status-success">✓ Stable</Box>
            )}
            {canDeprecate && item.status !== 'current' && (
              <ButtonDropdown
                items={[{ id: 'deprecate', text: 'Deprecate' }]}
                onItemClick={({ detail }) => { if (detail.id === 'deprecate') handleDeprecate(item); }}
                variant="inline-icon"
              />
            )}
          </SpaceBetween>
        );
      },
    },
  ];


  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Manage versioned product templates. Promote to Stable for production deploys."
        actions={<SpaceBetween direction="horizontal" size="xs">
          <Button onClick={() => selectedProduct?.value && loadVersions(selectedProduct.value)}>Refresh</Button>
          <Button variant="primary" onClick={() => { setNewVersion(''); setNewDesc(''); setNewChangelog(''); setShowCreate(true); }} disabled={!selectedProduct}>Register Version</Button>
        </SpaceBetween>}
      >Stack Versions</Header>

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}

      {/* Product selector + version summary KPIs */}
      <Container>
        <ColumnLayout columns={2}>
          <FormField label="Product">
            <Select selectedOption={selectedProduct} onChange={({ detail }) => setSelectedProduct(detail.selectedOption)} options={productOptions} />
          </FormField>
          <ColumnLayout columns={3} variant="text-grid">
            <SpaceBetween size="xxxs">
              <Box variant="awsui-key-label">Stable</Box>
              {stableVersion ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <code style={{ fontWeight: 700, fontSize: 16 }}>{stableVersion.version}</code>
                  <Badge color="green">Stable</Badge>
                </SpaceBetween>
              ) : (
                <Box color="text-status-inactive">None — promote a version</Box>
              )}
            </SpaceBetween>
            <SpaceBetween size="xxxs">
              <Box variant="awsui-key-label">Latest</Box>
              {latestVersion ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <code style={{ fontWeight: 700, fontSize: 16 }}>{latestVersion.version}</code>
                  <Badge color="blue">Latest</Badge>
                </SpaceBetween>
              ) : (
                <Box color="text-status-inactive">—</Box>
              )}
            </SpaceBetween>
            <SpaceBetween size="xxxs">
              <Box variant="awsui-key-label">Total Deployed</Box>
              <Box variant="awsui-value-large">{totalDeployed}</Box>
            </SpaceBetween>
          </ColumnLayout>
        </ColumnLayout>
      </Container>

      {/* Version lifecycle legend */}
      <Container>
        <SpaceBetween direction="horizontal" size="l">
          {(['draft', 'current', 'previous', 'deprecated'] as VersionStatus[]).map((s) => {
            const cfg = STATUS_DISPLAY[s];
            return (
              <SpaceBetween key={s} direction="horizontal" size="xxs">
                <Badge color={cfg.badge}>{cfg.label}</Badge>
                <Box fontSize="body-s" color="text-body-secondary">{cfg.description}</Box>
              </SpaceBetween>
            );
          })}
        </SpaceBetween>
      </Container>

      {/* Versions table */}
      <Table
        columnDefinitions={columns}
        items={visibleVersions}
        loading={loading}
        loadingText="Loading versions..."
        trackBy="version"
        variant="container"
        stripedRows
        stickyHeader
        empty={
          <Box textAlign="center" padding="xxl" color="text-status-inactive">
            No versions registered. Click "Register Version" to add the first one.
          </Box>
        }
        header={
          <Header counter={`(${visibleVersions.length})`}
            description={versions.length > visibleVersions.length ? `${versions.length - visibleVersions.length} deprecated version(s) hidden` : undefined}
          >Versions</Header>
        }
      />

      {/* Create version modal */}
      <Modal visible={showCreate} onDismiss={() => setShowCreate(false)} header="Register New Version"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} loading={submitting} disabled={!newVersion}>Register</Button>
        </SpaceBetween></Box>}>
        <SpaceBetween size="m">
          <Alert type="info">
            Register a version to track it in the registry. Upload the CFN template to
            S3 at <code>products/{selectedProduct?.value}/{'{version}'}/template.yaml</code> before
            promoting to Stable.
          </Alert>
          <FormField label="Version" constraintText="Semver format: 0.4.1, 1.0.0, etc.">
            <Input value={newVersion} onChange={({ detail }) => setNewVersion(detail.value)} placeholder="0.4.1" />
          </FormField>
          <FormField label="Description">
            <Input value={newDesc} onChange={({ detail }) => setNewDesc(detail.value)} placeholder="Bug fixes and performance improvements" />
          </FormField>
          <FormField label="Changelog">
            <Textarea value={newChangelog} onChange={({ detail }) => setNewChangelog(detail.value)} rows={3} placeholder="- Fixed IAM mapper edge case&#10;- Improved migration step ordering&#10;- Updated frontend dependencies" />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
